import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, COMPANY } from "../data/content";
import { scrollToId } from "../lib/scroll";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 150 : 0);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "border-b border-line bg-ink/85 backdrop-blur-md"
          : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-6 lg:px-12">
        <button
          data-testid="nav-logo-button"
          onClick={() => go("#top")}
          className="flex items-center gap-3"
          aria-label="Udat Marine home"
        >
          <img src="/images/logo-3d.png" alt="Udat Marine logo" className="h-9 w-auto" />
          <span className="hidden flex-col leading-none sm:flex">
            <span className="font-display text-sm font-semibold uppercase tracking-[0.14em]">
              Udat Marine
            </span>
            <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-steel">
              Sdn Bhd · Est. 2001
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.18em] text-steel transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-cta-phone"
            href={`tel:${COMPANY.phoneDuty.replace(/\s/g, "")}`}
            className="hidden border border-line px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white transition-colors hover:border-brand hover:text-brand md:block"
          >
            24/7 Duty · {COMPANY.phoneDuty}
          </a>
          <button
            data-testid="nav-cta-request"
            onClick={() => go("#contact")}
            className="hidden bg-brand px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-hover md:block"
          >
            Request Support
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="border border-line p-2.5 text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="border-t border-line bg-ink/95 backdrop-blur-md lg:hidden"
        >
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="block w-full border-b border-line px-6 py-4 text-left font-display text-lg uppercase tracking-tight text-slate-100 transition-colors hover:text-brand"
            >
              {l.label}
            </button>
          ))}
          <a
            href={`tel:${COMPANY.phoneDuty.replace(/\s/g, "")}`}
            className="block px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-brand"
          >
            24/7 Duty · {COMPANY.phoneDuty}
          </a>
        </div>
      )}
    </header>
  );
}
